import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/home/buttons';
import { Card } from '../../components/home/card';
import { ProgressBar } from '../../components/loader/progress-bar';
import { SpinTextLoader } from '../../components/loader/spin-loader';
import { getJobState } from '../../service/api.service';
import { JobState } from 'unvox-common';

type PreviewState = 'error' | 'validatingJobId' | 'waitingForPreview';

export function PreviewJobRoute() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const jobId = (params.jobId || '').trim();

    const [previewState, setPreviewState] = useState<PreviewState>('validatingJobId');

    if (!jobId) {
        return <Navigate to="/" replace />;
    }

    const [_ticker, setTicker] = useState(0);
    const isUnmountedRef = useRef(false);
    const timeoutRef = useRef<number>(undefined);

    async function request() {
        const result = await getJobState(jobId);
        if (result.error) {
            if (result.error.code === 'no_such_job') {
                navigate('/invalid-job', { replace: true });
            } else {
                setPreviewState('error');
            }

            return;
        }

        const data = result.data;
        if (!data) {
            return setPreviewState('error');
        }

        if (data.state === JobState.Failed) {
            return setPreviewState('error');
        }

        if (data.state === JobState.AwaitingProcessingTask || data.state === JobState.Completed) {
            return navigate(`/processing?jobId=${encodeURIComponent(jobId)}`, { replace: true });
        }

        if (data.previewUrl) {
            return navigate(`/payment/${encodeURIComponent(jobId)}`, { replace: true, state: { previewUrl: data.previewUrl } });
        }

        setPreviewState('waitingForPreview');
    };

    useEffect(() => {
        isUnmountedRef.current = false;

        function clear() {
            if (timeoutRef.current !== undefined) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = undefined;
            }
        };

        const tick = async (n: number) => {
            if (isUnmountedRef.current) return;

            setTicker(n);
            await request();
            if (isUnmountedRef.current) return;

            const nextDelay = n === 0 ? 60_000 : 15_000;

            timeoutRef.current = window.setTimeout(() => tick(n + 1), nextDelay);
        };

        tick(0);

        return () => {
            isUnmountedRef.current = true;
            clear();
        };
    }, []);

    if (previewState === 'error') {
        return (
            <Card>
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="space-y-2">
                        <h2 className="text-2xl font-semibold text-foreground">{t('route.previewJob.txtUnavailableTitle')}</h2>
                        <p className="text-sm text-secondary">{t('route.previewJob.txtUnavailableBody')}</p>
                    </div>
                    <Button variant="secondary" onClick={() => window.location.reload()} className="w-full">
                        {t('global.action.txtRetry')}
                    </Button>
                    <Button variant="primary" onClick={() => navigate('/')} className="w-full">
                        {t('global.action.txtStartOver')}
                    </Button>
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <div className="flex flex-col items-center gap-2 text-center">
                <h2 className="text-2xl font-semibold text-foreground">{t('route.previewJob.txtPreparingTitle')}</h2>
                <SpinTextLoader text={t(previewState === 'validatingJobId' ? 'global.job.txtCheckingStatus' : 'route.previewJob.txtPreparingBody')} />
                <ProgressBar className="mt-3 w-full max-w-sm" ariaLabel={t('route.previewJob.txtPreparingBody')} isActive={previewState === 'waitingForPreview'} initialDurationMs={60_000} initialTarget={80} />
                <div className="mt-2 text-xs text-secondary">
                    {t('global.job.txtIdLabel')}: <span className="text-secondary font-semibold">{jobId}</span>
                </div>
            </div>
        </Card>
    );
}
